// src/components/dentally/DentallyBookingFormPayment.jsx
import PropTypes from 'prop-types';
import {
  Box, Typography, ToggleButtonGroup, ToggleButton,
  TextField, Stack, InputAdornment,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Iconify from '../Iconify';

const PAYMENT_OPTIONS = [
  { value: 'none', label: 'No payment', icon: 'mdi:close-circle-outline' },
  { value: 'received', label: 'Payment received', icon: 'mdi:cash-check' },
  { value: 'link', label: 'Send payment link', icon: 'mdi:send-check' },
];

const PAYMENT_TYPE_OPTIONS = [
  { value: 'DEPOSIT', label: 'Deposit' },
  { value: 'CONSULTATION', label: 'Consultation fee' },
  { value: 'TREATMENT', label: 'Full treatment' },
];

const RECEIVED_MODE_OPTIONS = [
  { value: 'PHONE', label: 'Over the phone', icon: 'mdi:phone-outline' },
  { value: 'INPRACTICE', label: 'In practice', icon: 'mdi:store-outline' },
  { value: 'BANK_TRANSFER', label: 'Bank transfer', icon: 'mdi:bank-outline' },
  { value: 'OTHER', label: 'Other', icon: 'mdi:dots-horizontal-circle-outline' },
];

const LINK_CHANNEL_OPTIONS = [
  { value: 'email', label: 'Email', icon: 'mdi:email-outline' },
  { value: 'whatsapp', label: 'WhatsApp', icon: 'mdi:whatsapp' },
  { value: 'both', label: 'Email + WhatsApp', icon: 'mdi:email-plus-outline' },
];

const toggleBtnSx = (isLight) => ({
  borderRadius: '50px !important',
  border: '1px solid rgba(145, 158, 171, 0.24) !important',
  m: '4px 8px 4px 0px !important',
  p: '6px 14px 6px 10px',
  height: 36,
  color: isLight ? '#637381' : '#919EAB',
  bgcolor: isLight ? 'rgba(145, 158, 171, 0.04) !important' : 'transparent !important',
  fontWeight: 500,
  textTransform: 'none !important',
  fontSize: 13,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    bgcolor: 'rgba(0, 167, 111, 0.04) !important',
    borderColor: 'rgba(0, 167, 111, 0.32) !important',
  },
  '&.Mui-selected': {
    borderColor: '#00A76F !important',
    bgcolor: 'rgba(0, 167, 111, 0.08) !important',
    color: '#00A76F !important',
    fontWeight: 600,
  },
});

const smallToggleBtnSx = (isLight) => ({
  ...toggleBtnSx(isLight),
  height: 32,
  fontSize: 12,
  p: '4px 12px 4px 8px',
  m: '3px 6px 3px 0px !important',
});

export default function DentallyBookingFormPayment({ values, onChange, errors }) {
  const theme = useTheme();
  const isLight = theme.palette.mode === 'light';
  const selected = values.paymentOption || 'none';

  const receivedMode = values.receivedMode || 'PHONE';
  const paymentType = values.paymentType || 'DEPOSIT';
  const linkChannel = values.paymentLinkChannel || 'email';

  const receivedModeLabel = RECEIVED_MODE_OPTIONS.find(o => o.value === receivedMode)?.label || receivedMode;
  const paymentTypeLabel = PAYMENT_TYPE_OPTIONS.find(o => o.value === paymentType)?.label || paymentType;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant='subtitle2' sx={{ mb: 0.5, fontSize: 13, fontWeight: 600 }}>Payment</Typography>
      <ToggleButtonGroup
        color='primary'
        exclusive
        value={selected}
        onChange={(_, val) => { if (val !== null) onChange('paymentOption', val); }}
        sx={{ border: 'none', borderRadius: 0, flexWrap: 'wrap' }}
      >
        {PAYMENT_OPTIONS.map(({ value, label }) => (
          <ToggleButton key={value} value={value} sx={toggleBtnSx(isLight)}>
            <Iconify
              icon={selected === value ? 'bi:check-circle-fill' : 'material-symbols:circle'}
              width={16}
              sx={{ mr: 0.75, color: selected === value ? '#00A76F' : '#C4CDD5' }}
            />
            {label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      {values.paymentOption === 'received' && (
        <Box sx={{
          mt: 1.5, p: 2, borderRadius: 2,
          bgcolor: 'rgba(0,171,85,0.04)',
          border: '1px solid rgba(0,171,85,0.16)',
        }}>
          {/* Payment type */}
          <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 11, mb: 0.5, display: 'block' }}>
            Payment type
          </Typography>
          <ToggleButtonGroup
            color='primary'
            exclusive
            value={paymentType}
            onChange={(_, val) => { if (val !== null) onChange('paymentType', val); }}
            sx={{ border: 'none', borderRadius: 0, flexWrap: 'wrap', mb: 1.5 }}
          >
            {PAYMENT_TYPE_OPTIONS.map(({ value, label }) => (
              <ToggleButton key={value} value={value} sx={smallToggleBtnSx(isLight)}>
                <Iconify
                  icon={paymentType === value ? 'bi:check-circle-fill' : 'material-symbols:circle'}
                  width={14}
                  sx={{ mr: 0.5, color: paymentType === value ? '#00A76F' : '#C4CDD5' }}
                />
                {label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {/* Amount */}
          <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 11, mb: 0.5, display: 'block' }}>
            Amount
          </Typography>
          <TextField
            size="small"
            type="number"
            placeholder="0.00"
            InputProps={{
              startAdornment: <InputAdornment position="start">£</InputAdornment>,
              inputProps: { min: 0, step: 0.01, inputMode: 'decimal' },
            }}
            value={values.paymentAmount === '' || values.paymentAmount == null ? '' : values.paymentAmount}
            onChange={e => {
              const raw = e.target.value;
              if (raw === '') { onChange('paymentAmount', ''); }
              else { const parsed = parseFloat(raw); onChange('paymentAmount', Number.isNaN(parsed) ? '' : parsed); }
            }}
            error={!!errors.paymentAmount}
            helperText={errors.paymentAmount}
            sx={{ mb: 1.5, maxWidth: 180 }}
          />

          {/* How received */}
          <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 11, mb: 0.5, display: 'block' }}>
            How received
          </Typography>
          <ToggleButtonGroup
            color='primary'
            exclusive
            value={receivedMode}
            onChange={(_, val) => { if (val !== null) onChange('receivedMode', val); }}
            sx={{ border: 'none', borderRadius: 0, flexWrap: 'wrap' }}
          >
            {RECEIVED_MODE_OPTIONS.map(({ value, label, icon }) => (
              <ToggleButton key={value} value={value} sx={smallToggleBtnSx(isLight)}>
                <Iconify
                  icon={receivedMode === value ? 'bi:check-circle-fill' : icon}
                  width={14}
                  sx={{ mr: 0.5, color: receivedMode === value ? '#00A76F' : '#919EAB' }}
                />
                {label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {/* Receipt summary */}
          {values.paymentAmount > 0 && (
            <Box sx={{
              mt: 1.5, p: 1.5, borderRadius: 1.5,
              bgcolor: 'rgba(0,171,85,0.08)',
              border: '1px solid rgba(0,171,85,0.24)',
            }}>
              <Stack direction='row' alignItems='center' spacing={1}>
                <Iconify icon='mdi:receipt-text-check-outline' width={18} sx={{ color: '#00AB55', flexShrink: 0 }} />
                <Typography variant='body2' sx={{ fontSize: 12, color: 'text.secondary' }}>
                  <strong style={{ color: '#00AB55' }}>£{Number(values.paymentAmount).toFixed(2)}</strong>
                  {' '}{paymentTypeLabel.toLowerCase()} received {receivedModeLabel.toLowerCase()}
                </Typography>
              </Stack>
            </Box>
          )}
        </Box>
      )}

      {values.paymentOption === 'link' && (
        <Box sx={{
          mt: 1.5, p: 2, borderRadius: 2,
          bgcolor: 'rgba(7,141,238,0.04)',
          border: '1px solid rgba(7,141,238,0.16)',
        }}>
          {/* Payment type */}
          <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 11, mb: 0.5, display: 'block' }}>
            Payment type
          </Typography>
          <ToggleButtonGroup
            color='primary'
            exclusive
            value={paymentType}
            onChange={(_, val) => { if (val !== null) onChange('paymentType', val); }}
            sx={{ border: 'none', borderRadius: 0, flexWrap: 'wrap', mb: 1.5 }}
          >
            {PAYMENT_TYPE_OPTIONS.map(({ value, label }) => (
              <ToggleButton key={value} value={value} sx={smallToggleBtnSx(isLight)}>
                <Iconify
                  icon={paymentType === value ? 'bi:check-circle-fill' : 'material-symbols:circle'}
                  width={14}
                  sx={{ mr: 0.5, color: paymentType === value ? '#00A76F' : '#C4CDD5' }}
                />
                {label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {/* Amount */}
          <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 11, mb: 0.5, display: 'block' }}>
            Amount
          </Typography>
          <TextField
            size="small"
            type="number"
            placeholder="0.00"
            InputProps={{
              startAdornment: <InputAdornment position="start">£</InputAdornment>,
              inputProps: { min: 0, step: 0.01, inputMode: 'decimal' },
            }}
            value={values.paymentAmount === '' || values.paymentAmount == null ? '' : values.paymentAmount}
            onChange={e => {
              const raw = e.target.value;
              if (raw === '') { onChange('paymentAmount', ''); }
              else { const parsed = parseFloat(raw); onChange('paymentAmount', Number.isNaN(parsed) ? '' : parsed); }
            }}
            error={!!errors.paymentAmount}
            helperText={errors.paymentAmount}
            sx={{ mb: 1.5, maxWidth: 180 }}
          />

          {/* Send via */}
          <Typography variant='caption' sx={{ fontWeight: 600, color: 'text.secondary', fontSize: 11, mb: 0.5, display: 'block' }}>
            Send via
          </Typography>
          <ToggleButtonGroup
            color='primary'
            exclusive
            value={linkChannel}
            onChange={(_, val) => { if (val !== null) onChange('paymentLinkChannel', val); }}
            sx={{ border: 'none', borderRadius: 0, flexWrap: 'wrap' }}
          >
            {LINK_CHANNEL_OPTIONS.map(({ value, label, icon }) => (
              <ToggleButton key={value} value={value} sx={smallToggleBtnSx(isLight)}>
                <Iconify
                  icon={linkChannel === value ? 'bi:check-circle-fill' : icon}
                  width={14}
                  sx={{ mr: 0.5, color: linkChannel === value ? '#00A76F' : '#919EAB' }}
                />
                {label}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>

          {/* Link summary */}
          {values.paymentAmount > 0 && (
            <Box sx={{
              mt: 1.5, p: 1.5, borderRadius: 1.5,
              bgcolor: 'rgba(7,141,238,0.08)',
              border: '1px solid rgba(7,141,238,0.24)',
            }}>
              <Stack direction='row' alignItems='center' spacing={1}>
                <Iconify icon='mdi:send-check' width={18} sx={{ color: '#078DEE', flexShrink: 0 }} />
                <Typography variant='body2' sx={{ fontSize: 12, color: 'text.secondary' }}>
                  Payment link for <strong style={{ color: '#078DEE' }}>£{Number(values.paymentAmount).toFixed(2)}</strong>
                  {' '}({paymentTypeLabel.toLowerCase()}) will be sent via{' '}
                  {LINK_CHANNEL_OPTIONS.find(o => o.value === linkChannel)?.label || linkChannel}
                </Typography>
              </Stack>
            </Box>
          )}
        </Box>
      )}

    </Box>
  );
}

DentallyBookingFormPayment.propTypes = {
  values: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  errors: PropTypes.object,
};

DentallyBookingFormPayment.defaultProps = {
  errors: {},
};
